/**
 * The CRM financial wall — one choke point for rendering money.
 *
 * The requirement: when a staff member lacks financial access, they must see NO
 * monetary information ANYWHERE in the CRM — not just have the Invoices page
 * hidden, but every dollar figure on every page redacted (customer MRR/LTV,
 * prospect deal value, agreement/equipment prices, scheduled-invoice totals,
 * revenue dashboards). The reliable way to guarantee that is to route every
 * money render through ONE gate instead of trusting each page to remember.
 *
 * Use these everywhere money is shown on a CRM surface that a non-financial role
 * can reach:
 *
 *   - `<Money value={inv.total_amount} />`           — inline amount; redacts to
 *                                                       "—" without access.
 *   - `const fmt = useMoneyFormatter(); fmt(amount)` — when you need a string
 *                                                       (chart labels, aria, etc).
 *   - `<FinancialOnly>…</FinancialOnly>`             — hide a whole money block
 *                                                       (a Billing tab, a deals
 *                                                       panel, an MRR column).
 *   - `useFinancialAccess()`                          — the raw boolean.
 *
 * The gate keys on `financials:access`, which the dashboard grants ONLY to a
 * holder of the `crm:financials` permission (or super_admin) — see
 * server/src/auth/crmSessionPermissions.ts. The money-centric PAGES (Invoices,
 * Quotes, Financial Metrics) are additionally hidden from nav and route-guarded,
 * and the minted CRM token carries `crm_financials:false` so crm-api can omit
 * the fields server-side. This module is the LAST line — the one that catches a
 * money field that slipped onto an otherwise-allowed page.
 *
 * Pinned by money.test.tsx and the financial-wall safety-net test
 * (financial-wall.test.ts), which fails CI if a cross-cutting CRM page renders
 * `formatCurrency(...)` directly instead of going through here.
 */
import React from "react";

import usePermissions from "@crm-src/hooks/usePermissions";
import { formatCurrency, type FormatCurrencyOptions } from "@crm-src/lib/utils";

/** What an amount collapses to when the viewer has no financial access. An
 * em dash reads as "intentionally blank" rather than "$0" (which would be a
 * lie) or empty (which reads as a bug). */
export const REDACTED_MONEY = "—";

/** True when the current CRM viewer may see monetary information. Thin wrapper
 * over `usePermissions().canViewFinancials()` so the admin bypass + permission
 * logic lives in one place. */
export function useFinancialAccess(): boolean {
  const { canViewFinancials } = usePermissions();
  return canViewFinancials();
}

/** Second-arg shape shared with `formatCurrency` (boolean = legacy
 * includeSymbol, or the options object). */
type MoneyOptions = boolean | FormatCurrencyOptions;

/**
 * Returns a formatter that yields a formatted amount when the viewer has
 * financial access, or `REDACTED_MONEY` otherwise. Use when you need a STRING
 * (e.g. a recharts label, an aria-label, a PDF cell) rather than a node.
 */
export function useMoneyFormatter(): (
  amount: number | string | null | undefined,
  options?: MoneyOptions,
) => string {
  const allowed = useFinancialAccess();
  return (amount, options) =>
    allowed ? formatCurrency(amount, options) : REDACTED_MONEY;
}

export interface MoneyProps {
  /** The amount. Same accepted shapes as `formatCurrency`. */
  value: number | string | null | undefined;
  /** Formatting options (or the legacy `includeSymbol` boolean). */
  options?: MoneyOptions;
  /** Override the redacted placeholder (defaults to "—"). */
  redacted?: React.ReactNode;
  /** Optional className for the wrapping span. */
  className?: string;
}

/**
 * Inline money. Renders the formatted amount when the viewer has financial
 * access, otherwise the redacted placeholder. Wrapped in a span carrying a
 * `data-redacted` attribute so tests (and any future audit) can assert the wall
 * held without scraping the visible glyph.
 */
export function Money({
  value,
  options,
  redacted = REDACTED_MONEY,
  className,
}: MoneyProps): React.ReactElement {
  const allowed = useFinancialAccess();
  if (!allowed) {
    return (
      <span className={className} data-redacted="financials" title="Hidden — no financial access">
        {redacted}
      </span>
    );
  }
  return <span className={className}>{formatCurrency(value, options)}</span>;
}

export interface FinancialOnlyProps {
  children: React.ReactNode;
  /** Rendered instead of `children` when the viewer lacks financial access.
   * Defaults to nothing (the block disappears entirely). */
  fallback?: React.ReactNode;
}

/**
 * Renders its children only for a viewer with financial access. Use to drop a
 * whole monetary block — a Billing tab, a deals/invoices panel, an MRR column,
 * a revenue stat card — rather than redacting each figure inside it.
 */
export function FinancialOnly({
  children,
  fallback = null,
}: FinancialOnlyProps): React.ReactElement {
  const allowed = useFinancialAccess();
  return <>{allowed ? children : fallback}</>;
}
